import { basicInfoAtom } from "@/atoms"
import authSheetAtom from "@/atoms/auth-sheet"
import { Heading, Text } from "@/components/ui"
import { drawPrize, type PrizeDraw, type PrizeDrawStatus, type PrizeGame } from "@/domain/marketing/api"
import { prizeGameKind } from "@/domain/marketing/prize-game"
import { nextChanceLabel } from "@/domain/marketing/prize-game-period"
import * as Dialog from "@radix-ui/react-dialog"
import { useAtomValue, useSetAtom } from "jotai"
import { useReducedMotion } from "motion/react"
import { useEffect, useRef, useState } from "react"
import { PrizeGameLink, PrizeGameButton } from "./PrizeGameButton"
import { PrizeGameSheet } from "./PrizeGameSheet"
import { PrizeLoginPrompt } from "./PrizeLoginPrompt"
import { PrizeResult } from "./PrizeResult"
import { PrizeScratchCard } from "./PrizeScratchCard"
import { ScratchPrize } from "./ScratchTicket"
import { PrizeWheel } from "./PrizeWheel"

type Phase =
  | { kind: "idle" }
  | { kind: "drawing" }
  | { kind: "drawn"; draw: PrizeDraw }
  | { kind: "revealed"; draw: PrizeDraw }
  | { kind: "limited"; draw: PrizeDraw }
  | { kind: "error" }

type PrizeGameModalProps = {
  game: PrizeGame
  open: boolean
  authenticated: boolean
  status?: PrizeDrawStatus | null
  onOpenChange: (open: boolean) => void
  onPlayed: () => void
}

function limitMessage(reason: string | undefined, nextChanceAt: string | undefined): string {
  const next = nextChanceLabel(nextChanceAt)
  switch (reason) {
    case "IP_DAILY_CAP":
    case "DEVICE_DAILY_CAP":
      return "Você já jogou hoje. Volte amanhã para uma nova chance."
    case "PHONE_ALREADY_PLAYED":
      return next ? `Este número já jogou. ${next}.` : "Este número já jogou nesta rodada."
    case "PRIOR_ORDER_REQUIRED":
      return "Este jogo é para quem já pediu na loja. Faça seu pedido e volte para jogar."
    default:
      return next ? `Você já jogou. ${next}.` : "Você já jogou hoje."
  }
}

function statusLine(phase: Phase, isWheel: boolean, authenticated: boolean): string | null {
  switch (phase.kind) {
    case "drawing":
      return isWheel ? "Sorteando…" : "Preparando seu cartão…"
    case "drawn":
      return isWheel ? "Girando a roleta…" : "Raspe com o dedo para ver o seu prêmio."
    case "error":
      return isWheel ? "Não deu pra sortear agora. Tente de novo." : "Não conseguimos carregar seu cartão."
    case "revealed":
      return phase.draw.won ? `Você ganhou: ${phase.draw.prizeLabel ?? "um prêmio"}` : "Não foi dessa vez."
    case "limited":
      return limitMessage(phase.draw.limitReason, phase.draw.nextChanceAt)
    case "idle":
      return !isWheel && authenticated ? "Preparando seu cartão…" : null
  }
}

/**
 * Wheel or scratch card, by the game's type. The draw happens on the server
 * (a repeated draw in the window replays the same outcome; abuse caps answer
 * calmly); the widget only animates to it. A visitor who taps play is asked
 * to sign in (a gentle inline prompt, never a draw before login) and the play
 * continues right after. When the server says the
 * customer already played, it shows the next chance and the unused prize. A
 * win follows its coupon until it is issued and offers one way to spend it
 * (see `PrizeRedeemCard`).
 */
export function PrizeGameModal({ game, open, authenticated, status, onOpenChange, onPlayed }: PrizeGameModalProps) {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" })
  const [wantsPlay, setWantsPlay] = useState(false)
  const [autoScratch, setAutoScratch] = useState(false)
  const [promptOpen, setPromptOpen] = useState(false)
  const uncovered = useRef(false)
  const inFlight = useRef(false)
  const basicInfo = useAtomValue(basicInfoAtom)
  const setAuthSheet = useSetAtom(authSheetAtom)
  const reduceMotion = useReducedMotion() ?? false
  const isWheel = prizeGameKind(game.type) === "wheel"
  const storeName = basicInfo?.name ?? ""
  const logoUrl = basicInfo?.logoImage?.URL || undefined
  const drawn = phase.kind === "drawn" || phase.kind === "revealed" ? phase.draw : null
  const revealed = phase.kind === "revealed"
  const blocked = authenticated && phase.kind === "idle" && status != null && !status.canPlay
  const settled = revealed || phase.kind === "limited" || blocked
  const unusedPrize = blocked && status?.lastDraw?.won && status.lastDraw.code && !status.lastDraw.redeemed ? status.lastDraw : null

  function reveal(draw: PrizeDraw) {
    setPhase({ kind: "revealed", draw })
    onPlayed()
  }

  async function play() {
    if (inFlight.current || (phase.kind !== "idle" && phase.kind !== "error")) return
    inFlight.current = true
    setPhase({ kind: "drawing" })
    try {
      const draw = await drawPrize(game.id)
      if (draw.limited) {
        setPhase({ kind: "limited", draw })
        return
      }
      if (!isWheel && uncovered.current) reveal(draw)
      else setPhase({ kind: "drawn", draw })
    } catch {
      setPhase({ kind: "error" })
    } finally {
      inFlight.current = false
    }
  }

  function uncover() {
    uncovered.current = true
    if (phase.kind === "drawn") reveal(phase.draw)
  }

  const latestPlay = useRef(play)
  useEffect(() => {
    latestPlay.current = play
  })

  useEffect(() => {
    if (!open || !authenticated || blocked || phase.kind !== "idle") return
    if (!isWheel || wantsPlay) void latestPlay.current()
  }, [open, authenticated, blocked, isWheel, wantsPlay, phase.kind])

  function playOrAsk() {
    if (authenticated) {
      void play()
      return
    }
    setPromptOpen(true)
  }

  function signIn() {
    setWantsPlay(true)
    setAuthSheet({ open: true })
  }

  const promptVisible = !authenticated && !revealed && promptOpen

  const title = game.name || (isWheel ? "Roleta de prêmios" : "Raspadinha")
  const line = blocked ? limitMessage(status?.limitReason, status?.nextChanceAt) : statusLine(phase, isWheel, authenticated)
  const lineVisible = blocked || phase.kind === "error" || phase.kind === "limited" || (!isWheel && authenticated && !revealed)
  const revealedNextChance = revealed ? nextChanceLabel(phase.draw.nextChanceAt) : null

  return (
    <PrizeGameSheet
      open={open}
      onOpenChange={onOpenChange}
      onEscapeKeyDown={(event) => {
        if (!promptVisible) return
        event.preventDefault()
        setPromptOpen(false)
      }}
    >
      <div className="flex flex-col items-center gap-5 pt-1 text-center">
        <div className="flex flex-col items-center gap-1">
          <Dialog.Title asChild>
            <Heading level={2} size="page" leading="tight" family="game-display">
              {title}
            </Heading>
          </Dialog.Title>
          <Text size="sm" tone="muted">
            {isWheel ? "Gire a roleta e ganhe um prêmio." : "Raspe e descubra o seu prêmio."}
          </Text>
        </div>

        <div className="w-full py-1">
          {isWheel ? (
            <PrizeWheel prizes={game.prizes} missPossible={game.missPossible !== false} draw={drawn} logoUrl={logoUrl} storeName={storeName} onSpinEnd={uncover} />
          ) : (
            <PrizeScratchCard
              playable={authenticated && phase.kind === "drawn" && !autoScratch && !blocked}
              revealed={revealed}
              autoScratch={autoScratch}
              logoUrl={logoUrl}
              storeName={storeName}
              onScratchStart={() => undefined}
              onLockedTouch={authenticated ? undefined : playOrAsk}
              onUncovered={uncover}
            >
              {drawn ? <ScratchPrize won={drawn.won} label={drawn.prizeLabel ?? "Um prêmio"} artKey={drawn.prizeArtKey} /> : null}
            </PrizeScratchCard>
          )}
        </div>

        <div className="flex w-full flex-col items-stretch gap-2">
          <Text role="status" aria-live="polite" size="sm" tone="muted" className={lineVisible ? undefined : "sr-only"}>
            {line}
          </Text>
          {phase.kind === "revealed" ? <PrizeResult draw={phase.draw} headline={isWheel} onLeave={() => onOpenChange(false)} /> : null}
          {revealedNextChance ? (
            <Text size="xs" tone="muted">
              {revealedNextChance}
            </Text>
          ) : null}
          {unusedPrize ? <PrizeResult draw={unusedPrize} onLeave={() => onOpenChange(false)} /> : null}
          {promptVisible ? <PrizeLoginPrompt game={isWheel ? "wheel" : "scratch"} onSignIn={signIn} onDismiss={() => setPromptOpen(false)} /> : null}
          {!authenticated && !revealed && !promptVisible ? (
            <PrizeGameButton onClick={playOrAsk}>{isWheel ? "Girar a roleta" : "Raspar o cartão"}</PrizeGameButton>
          ) : null}
          {authenticated && isWheel && !settled && (phase.kind === "idle" || phase.kind === "error") ? (
            <PrizeGameButton onClick={playOrAsk}>Girar a roleta</PrizeGameButton>
          ) : null}
          {authenticated && isWheel && phase.kind === "drawing" ? <PrizeGameButton loading>Sorteando…</PrizeGameButton> : null}
          {authenticated && isWheel && phase.kind === "drawn" ? <PrizeGameButton disabled>Girando…</PrizeGameButton> : null}
          {authenticated && !isWheel && phase.kind === "error" ? <PrizeGameButton onClick={() => void play()}>Tentar de novo</PrizeGameButton> : null}
          {authenticated && !isWheel && phase.kind !== "error" && !settled ? (
            <PrizeGameButton disabled={phase.kind !== "drawn" || autoScratch} onClick={() => setAutoScratch(true)}>
              {reduceMotion ? "Revelar" : "Raspar o cartão"}
            </PrizeGameButton>
          ) : null}
          {promptVisible ? null : <PrizeGameLink onClick={() => onOpenChange(false)}>{settled ? "Fechar" : "Agora não"}</PrizeGameLink>}
        </div>
      </div>
    </PrizeGameSheet>
  )
}
